import React, { useEffect, useState } from 'react';
import { Table, Button, Stack, IconButton, Toggle, Whisper, Tooltip } from 'rsuite';
import EditIcon from '@rsuite/icons/Edit';
import ListIcon from '@rsuite/icons/List';
import { AxiosError } from 'axios';
import PageContent from '@/components/PageContent';
import { BaseErrorModel } from '@/models/core.model';
import { AttendeeConditionList, PointEarnPeriodList, PointEarnRule, PointEarnTrigger, PointEarnTriggerList, getRuleAttendee, getRuleVisit, isEnergyHubTrigger } from '@/models/point-account.model';
import { getPointEarnRules, setPointEarnRuleActive } from '@/services/point-account.service';
import { toastErrorMessage } from '@/utils/toasterMessage';
import PointEarnRuleDrawer from './point-earn-rule-drawer';
import PointEarnRecordDrawer from './point-earn-record-drawer';

const { Column, HeaderCell, Cell } = Table;

interface Props {
  pointAccountId: number;
}

const PointEarnRuleList: React.FC<Props> = ({ pointAccountId }) => {
  const [items, setItems] = useState<PointEarnRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [editRule, setEditRule] = useState<PointEarnRule | undefined>(undefined);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [recordsRule, setRecordsRule] = useState<PointEarnRule | undefined>(undefined);
  const [togglingId, setTogglingId] = useState<number | undefined>(undefined);

  useEffect(() => {
    fetchData();
  }, [pointAccountId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await getPointEarnRules(pointAccountId);
      if (result.success) setItems(result.result ?? []);
    } catch (err) {
      setItems([]);
      const em = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching earn rules: ${em?.errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (rule: PointEarnRule, isActive: boolean) => {
    setTogglingId(rule.id);
    try {
      await setPointEarnRuleActive(pointAccountId, rule.id, isActive);
      setItems(pre => pre.map(r => r.id == rule.id ? { ...r, isActive } : r));
    } catch (err) {
      const em = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in updating rule: ${em?.errorMessage}`);
    } finally {
      setTogglingId(undefined);
    }
  };

  const limitText = (r: PointEarnRule) => {
    const parts: string[] = [];
    if (r.maxPerUser) parts.push(`${r.maxPerUser} lifetime`);
    if (r.maxPerUserPerPeriod) parts.push(`${r.maxPerUserPerPeriod} / ${PointEarnPeriodList.find(p => p.value == r.period)?.label ?? '?'}`);
    return parts.length ? parts.join(', ') : 'Unlimited';
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={() => { setEditRule(undefined); setOpenDrawer(true); }}>
          Add Earn Rule
        </Button>
      </Stack>
      <Table autoHeight data={items} loading={loading}>
        <Column flexGrow={0.5}>
          <HeaderCell>Priority</HeaderCell>
          <Cell dataKey="priority" />
        </Column>
        <Column flexGrow={2} fullText>
          <HeaderCell>Name</HeaderCell>
          <Cell dataKey="name" />
        </Column>
        <Column flexGrow={1.2}>
          <HeaderCell>Trigger</HeaderCell>
          <Cell>{(r: PointEarnRule) => PointEarnTriggerList.find(t => t.value == r.trigger)?.label ?? r.trigger}</Cell>
        </Column>
        <Column flexGrow={1}>
          <HeaderCell>Condition</HeaderCell>
          <Cell>{(r: PointEarnRule) => r.trigger == PointEarnTrigger.ClubEventCheckIn
            ? AttendeeConditionList.find(a => a.value == getRuleAttendee(r))?.value
            : isEnergyHubTrigger(r.trigger) ? getRuleVisit(r) : '—'}</Cell>
        </Column>
        <Column flexGrow={0.6} align="right">
          <HeaderCell>Amount</HeaderCell>
          <Cell dataKey="amount" />
        </Column>
        <Column flexGrow={0.8} align="center">
          <HeaderCell>Expires (days)</HeaderCell>
          <Cell>{(r: PointEarnRule) => r.expiresAfterDays ?? 'Never'}</Cell>
        </Column>
        <Column flexGrow={1.4} fullText>
          <HeaderCell>Per-user limit</HeaderCell>
          <Cell>{(r: PointEarnRule) => limitText(r)}</Cell>
        </Column>
        <Column flexGrow={0.7} align="center">
          <HeaderCell>Active</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(r: PointEarnRule) => (
              <Toggle size="sm" checked={r.isActive} loading={togglingId == r.id} onChange={checked => handleToggle(r, checked)} />
            )}
          </Cell>
        </Column>
        <Column flexGrow={0.8} fixed="right">
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(r: PointEarnRule) => (
              <Stack spacing={4}>
                <IconButton icon={<EditIcon />} circle size="sm" onClick={() => { setEditRule(r); setOpenDrawer(true); }} />
                <Whisper speaker={<Tooltip>Earn records</Tooltip>}>
                  <IconButton icon={<ListIcon />} circle size="sm" onClick={() => setRecordsRule(r)} />
                </Whisper>
              </Stack>
            )}
          </Cell>
        </Column>
      </Table>

      <PointEarnRuleDrawer open={openDrawer} pointAccountId={pointAccountId} rule={editRule}
        onClose={() => setOpenDrawer(false)} onSubmitted={() => { setOpenDrawer(false); fetchData(); }} />
      <PointEarnRecordDrawer open={!!recordsRule} pointAccountId={pointAccountId} rule={recordsRule}
        onClose={() => setRecordsRule(undefined)} />
    </PageContent>
  );
};

export default PointEarnRuleList;
